import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Switch } from 'antd';

import { scan } from '../../platform';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * The camera.
 *
 * WHAT THIS IS CAREFUL ABOUT, in the order the failures actually happen:
 *
 * 1. THE DETECTOR. Chosen by capability, never by presence — see
 *    utils/barcode/detector.js. The modal says which one it got, because a
 *    reader who knows the WASM reader is loading knows why the first scan
 *    took a second.
 * 2. PERMISSION. getUserMedia rejects when the reader says no, when the
 *    context is not secure, and when another app holds the camera. Each is a
 *    sentence, and ALL of them still leave the manual-entry box, which is
 *    the whole reason it is here rather than behind a link.
 * 3. THE LOOP. requestAnimationFrame rather than setInterval, so it stops
 *    when the tab is hidden instead of holding a camera in the background,
 *    and one decode in flight at a time.
 * 4. THE TEARDOWN. Every track is stopped on close, on unmount, and on a
 *    failed start. A camera light left on after a modal closes is the bug
 *    everybody remembers.
 *
 * The modal hands back a STRING. What it means — a text value, a record to
 * look up — belongs to the caller, because only the caller knows what the
 * field is.
 */

export default function ScanModal({ open, onClose, onDecode, formats = [], title = 'Scan a code' }) {
	const videoRef = useRef(null);
	const streamRef = useRef(null);
	const frameRef = useRef(0);
	const liveRef = useRef(false);

	const [error, setError] = useState(null);
	const [kind, setKind] = useState(null);
	const [torchOn, setTorchOn] = useState(false);
	const [torchable, setTorchable] = useState(false);
	const [manual, setManual] = useState('');
	// More than one code in the frame — see the decode loop.
	const [crowded, setCrowded] = useState(false);

	const stop = useCallback(() => {
		liveRef.current = false;
		if (frameRef.current) cancelAnimationFrame(frameRef.current);
		frameRef.current = 0;
		const stream = streamRef.current;
		streamRef.current = null;
		if (stream) stream.getTracks().forEach(track => track.stop());
	}, []);

	const finish = useCallback(value => {
		stop();
		onDecode(value);
	}, [onDecode, stop]);

	useEffect(() => {
		if (!open) {
			stop();
			return undefined;
		}
		setError(null);
		setManual('');
		setCrowded(false);
		setTorchOn(false);
		setTorchable(false);
		liveRef.current = true;

		let detector = null;

		(async () => {
			const availability = scan.availability();
			if (!availability.available) {
				setError(availability.reason === 'insecure-context'
					? 'The camera needs a secure connection (https, or localhost in development). Type the code instead.'
					: 'This browser will not give the page a camera. Type the code instead.');
				return;
			}

			try {
				detector = await scan.detector(formats);
				if (!liveRef.current) return;
				setKind(detector.kind);
			} catch (e) {
				if (!liveRef.current) return;
				setError('The barcode reader could not be loaded. Type the code instead.');
				return;
			}

			let stream;
			try {
				// The BACK camera: a person scanning a truck is not looking at
				// their own face. `ideal` rather than `exact` so a laptop with
				// one camera still gets it.
				stream = await navigator.mediaDevices.getUserMedia({
					video: { facingMode: { ideal: 'environment' } },
					audio: false,
				});
			} catch (e) {
				if (!liveRef.current) return;
				const name = e && e.name;
				setError(
					name === 'NotAllowedError' ? 'Camera access was refused. Allow it in the browser, or type the code instead.'
						: name === 'NotFoundError' ? 'No camera was found. Type the code instead.'
						: name === 'NotReadableError' ? 'The camera is in use by another app. Type the code instead.'
						: 'The camera could not be started. Type the code instead.'
				);
				return;
			}

			if (!liveRef.current) {
				stream.getTracks().forEach(track => track.stop());
				return;
			}
			streamRef.current = stream;

			const [track] = stream.getVideoTracks();
			const capabilities = track && typeof track.getCapabilities === 'function' ? track.getCapabilities() : {};
			setTorchable(!!(capabilities && capabilities.torch));

			const video = videoRef.current;
			if (video) {
				video.srcObject = stream;
				try {
					await video.play();
				} catch (e) {
					// Autoplay refusals are recoverable by the reader tapping
					// the frame; nothing else to do about it here.
				}
			}

			let busy = false;
			const tick = async () => {
				if (!liveRef.current) return;
				frameRef.current = requestAnimationFrame(tick);
				if (busy || !videoRef.current || videoRef.current.readyState < 2) return;
				busy = true;
				try {
					const results = await detector.detect(videoRef.current);
					if (!liveRef.current) return;
					// THE RETICLE IS CHROME. The decode runs on the whole
					// video frame, so a pallet label carrying a part number
					// and a bin tag decodes BOTH in one frame — and the
					// reader's aim has nothing to do with the order the
					// results come back in. Taking [0] there is a wrong
					// write that looks like a right one, which is the worst
					// thing this feature can do; so a frame holding more
					// than one code is not a decode, it is a nudge to move
					// the camera. (A blank rawValue is skipped rather than
					// throwing the frame away with it.)
					const values = [...new Set(
						(Array.isArray(results) ? results : [])
							.map(result => String(result?.rawValue == null ? '' : result.rawValue))
							.filter(Boolean)
					)];
					setCrowded(values.length > 1);
					if (values.length === 1) {
						finish(values[0]);
						return;
					}
				} catch (e) {
					// A frame that cannot be decoded is the normal case, not an
					// error: the loop simply tries the next one.
				} finally {
					busy = false;
				}
			};
			frameRef.current = requestAnimationFrame(tick);
		})();

		return stop;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => stop, [stop]);

	const toggleTorch = async next => {
		setTorchOn(next);
		const stream = streamRef.current;
		const [track] = stream ? stream.getVideoTracks() : [];
		if (!track || typeof track.applyConstraints !== 'function') return;
		try {
			await track.applyConstraints({ advanced: [{ torch: next }] });
		} catch (e) {
			setTorchable(false);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={() => { stop(); onClose(); }}
			footer={null}
			title={title}
			destroyOnClose
			width={420}
			centered
		>
			<div className="scan-modal">
				{error ? (
					<Alert type="warning" showIcon message={error} data-testid="scan-error" />
				) : (
					<div className="scan-modal__viewfinder">
						{/* muted + playsInline are what let iOS play this at all. */}
						<video ref={videoRef} muted playsInline autoPlay data-testid="scan-video" />
						<div className="scan-modal__reticle" aria-hidden="true" />
					</div>
				)}

				{!error && crowded ? (
					<Alert
						type="info"
						showIcon
						message="More than one code in view"
						description="Move the camera so only the one you want is in the frame."
						data-testid="scan-crowded"
					/>
				) : null}

				{!error && kind === 'wasm' ? (
					<p className="form-panel__hint" data-testid="scan-kind">
						Using the built-in reader — this browser has no barcode support of its own.
					</p>
				) : null}

				{torchable ? (
					<Space>
						<Switch checked={torchOn} onChange={toggleTorch} id="scan-torch" />
						<label htmlFor="scan-torch">Light</label>
					</Space>
				) : null}

				{/* The escape, always present: a torn label, a dark yard, a
				    browser with no camera. Typing it is not a fallback for a
				    broken feature, it is part of the feature. */}
				<Space.Compact block className="input-affix-group">
					<Input
						{...NO_TEXT_ASSIST}
						className="input-affix-group__input"
						value={manual}
						onChange={event => setManual(event.target.value)}
						onPressEnter={() => manual.trim() && finish(manual.trim())}
						placeholder="Or type the code"
						aria-label="Type the code"
					/>
					<Button type="primary" disabled={!manual.trim()} onClick={() => finish(manual.trim())}>
						Use
					</Button>
				</Space.Compact>
			</div>
		</Modal>
	);
}
